// src/components/navigation/MobileNav.tsx - Fresh complete component
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  ListItemButton,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { BaseNavProps } from '../../types/navigation';

const MobileNav: React.FC<BaseNavProps> = ({
  navigation,
  userEmail,
  logo,
  UserMenu,
  role,
  onLogout,
  searchComponent
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [expandedSubSection, setExpandedSubSection] = useState<string | null>(null);

  const handleMobileSectionClick = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
    setExpandedSubSection(null);
  };

  const handleMobileSubSectionClick = (section: string) => {
    setExpandedSubSection(expandedSubSection === section ? null : section);
  };

  // Render the icon safely
  const renderIcon = (icon: React.ReactNode | React.ComponentType<any>) => {
    if (!icon) return null;
    if (typeof icon === 'function') {
      const IconComponent = icon;
      return <IconComponent />;
    }
    if (React.isValidElement(icon)) {
      return icon;
    }
    return null;
  };

  return (
    <>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          {/* Left side - Menu button + Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setMobileMenuOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon className="w-6 h-6" />
            </IconButton>
            {logo}
          </Box>

          {/* Right side - User menu */}
          {userEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <UserMenu userEmail={userEmail} role={role} onLogout={onLogout} />
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        ModalProps={{ keepMounted: true }}
      >
        <Box sx={{ width: 280 }}>
          {/* Search in drawer */}
          {searchComponent && (
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
              {searchComponent}
            </Box>
          )}

          {/* Main navigation items */}
          <List>
            {navigation.mainItems.map((item) => {
              if (item.children) {
                return (
                  <Box key={item.label}>
                    <ListItemButton onClick={() => handleMobileSectionClick(item.label)}>
                      <ListItemIcon>{renderIcon(item.icon)}</ListItemIcon>
                      <ListItemText primary={item.label} />
                      {expandedSection === item.label ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </ListItemButton>
                    <Collapse in={expandedSection === item.label}>
                      <List sx={{ pl: 2 }}>
                        {item.children.map((child) =>
                          child.children ? (
                            <Box key={child.label}>
                              <ListItemButton onClick={() => handleMobileSubSectionClick(child.label)} sx={{ pl: 2 }}>
                                <ListItemText primary={child.label} />
                                {expandedSubSection === child.label ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </ListItemButton>
                              <Collapse in={expandedSubSection === child.label}>
                                <List sx={{ pl: 2 }}>
                                  {child.children.map((grandchild) => (
                                    <ListItemButton key={grandchild.path || grandchild.label} component={RouterLink} to={grandchild.path || ''}
                                      onClick={() => setMobileMenuOpen(false)} sx={{ pl: 4 }}>
                                      <ListItemText primary={grandchild.label} />
                                    </ListItemButton>
                                  ))}
                                </List>
                              </Collapse>
                            </Box>
                          ) : (
                            <ListItemButton key={child.path || child.label} component={RouterLink} to={child.path || ''}
                              onClick={() => setMobileMenuOpen(false)} sx={{ pl: 4 }}>
                              <ListItemText primary={child.label} />
                            </ListItemButton>
                          )
                        )}
                      </List>
                    </Collapse>
                  </Box>
                );
              }
              return (
                <ListItemButton
                  key={item.path || item.label}
                  component={RouterLink}
                  to={item.path || ''}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <ListItemIcon>{renderIcon(item.icon)}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
            })}
          </List>

          {/* Dropdown sections */}
          {navigation.dropdowns && (
            <>
              <Divider />
              <List>
                {Object.entries(navigation.dropdowns).map(([section, items]) => (
                  <Box key={section}>
                    <ListItemButton onClick={() => handleMobileSectionClick(section)}>
                      <ListItemText primary={section} />
                      {expandedSection === section ? 
                        <ChevronUp className="w-4 h-4" /> : 
                        <ChevronDown className="w-4 h-4" />
                      }
                    </ListItemButton>
                    <Collapse in={expandedSection === section}>
                      <List sx={{ pl: 2 }}>
                        {items.map((item) => 
                          item.children ? (
                            <Box key={item.label}>
                              <ListItemButton
                                onClick={() => handleMobileSubSectionClick(item.label)}
                                sx={{ pl: 2 }}
                              >
                                <ListItemText primary={item.label} />
                                {expandedSubSection === item.label ?
                                  <ChevronUp className="w-4 h-4" /> :
                                  <ChevronDown className="w-4 h-4" />
                                }
                              </ListItemButton>
                              <Collapse in={expandedSubSection === item.label}>
                                <List sx={{ pl: 2 }}>
                                  {item.children.map((child) => (
                                    <ListItemButton
                                      key={child.path || child.label}
                                      component={RouterLink}
                                      to={child.path || ''}
                                      onClick={() => setMobileMenuOpen(false)}
                                      sx={{ pl: 4 }}
                                    >
                                      <ListItemText primary={child.label} />
                                    </ListItemButton>
                                  ))}
                                </List>
                              </Collapse>
                            </Box>
                          ) : (
                            <ListItemButton
                              key={item.path || item.label}
                              component={RouterLink}
                              to={item.path || ''}
                              onClick={() => setMobileMenuOpen(false)}
                              sx={{ pl: 4 }}
                            >
                              <ListItemText primary={item.label} />
                            </ListItemButton>
                          )
                        )}
                      </List>
                    </Collapse>
                  </Box>
                ))}
              </List>
            </>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default MobileNav;